import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  type Contact,
  ContactSchema,
  type ContactFormValues,
  type ContactAlternative,
  type ContactAlternativeFormValues,
} from "../models/contact"
import { contactApi } from "../services/contactApi"
import { useCrudList, useCrudItem } from "../../../hooks/useCrud"

const BASE_KEY = "contacts"
const BASE_URL = "contact"

export function useContacts(filters?: Record<string, unknown>) {
  return useCrudList<Contact>(BASE_KEY, BASE_URL, ContactSchema, filters)
}

export function useContact(id?: number | null) {
  return useCrudItem<Contact>(BASE_KEY, BASE_URL, ContactSchema, id)
}

export function useContactTypes() {
  return useQuery({
    queryKey: [BASE_KEY, "types"],
    queryFn: () => contactApi.getTypes(),
    staleTime: 1000 * 60 * 30,
  })
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation<Contact, Error, ContactFormValues>({
    mutationFn: (values) => contactApi.create(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateContact() {
  const queryClient = useQueryClient()
  return useMutation<Contact, Error, { id: number; values: ContactFormValues }>({
    mutationFn: ({ id, values }) => contactApi.update(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteContact() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => contactApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useCreateAlternativeContact() {
  const queryClient = useQueryClient()
  return useMutation<ContactAlternative, Error, ContactAlternativeFormValues>({
    mutationFn: (values) => contactApi.createAlternative(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateAlternativeContact() {
  const queryClient = useQueryClient()
  return useMutation<ContactAlternative, Error, { id: number; values: ContactAlternativeFormValues }>({
    mutationFn: ({ id, values }) => contactApi.updateAlternative(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteAlternativeContact() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => contactApi.deleteAlternative(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}
